import { useState, useEffect } from 'react';
import { useBankCatalog } from '@/hooks/useBankCatalog';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { ArrowRight, CreditCard, Banknote, Loader2, QrCode, CheckCircle, AlertTriangle } from 'lucide-react';
import { toast } from "sonner";
import { PaymentService } from '@/services/paymentService';
import { isMoneyTransferEnabled } from '@/config/features';
import { useCompanySyncStatus, isSyncStillImporting, isSyncStopped } from '@/hooks/useCompanySyncStatus';
import BankAccountChartMappingCell from './BankAccountChartMappingCell';
import { useConsentContextOptional } from '@/components/providers/ConsentProvider';
import { renewalRequestFor } from '@/lib/renewalRequest';
import { useBankAccountType } from '@/i18n/useBankAccountType';
import { useTranslations } from 'use-intl';
import { userFacingErrorMessage } from '@/utils/userFacingError';
import { formatAmount } from "@financialharmony/ui";

interface BankAccount {
  id: string;
  bankName: string;
  // FIN-911: display this, never accountNumber (which can hold the full IBAN).
  maskedNumber: string | null;
  balance: number | null;
  currency: string;
  logoUrl?: string;
  bic: string;
  // FIN-885: a bic-less bank's identity, which renewing its access is keyed on.
  providerRef?: string | null;
  countryCode?: string | null;
  resourceId?: string;
  syncFailed?: boolean;
  status?: 'ok' | 'unavailable' | 'consent_invalid';
  chartAccountNumber?: string;
  chartAccountName?: string;
  chartMappingLocked?: boolean;
  chartMappingLockedEntryCount?: number;
  chartMappingLockedFirstEntryDate?: string;
  chartMappingCandidates?: Array<{ number: string; ledgerEntryCount: number; firstEntryDate: string | null }>;
  chartMappingSwapOnly?: boolean;
  cashAccountType?: string | null;
  // FIN-1567 AC8: renders the Personal chip below.
  accountType?: 'business' | 'personal';
}

interface BankAccountsDisplayProps {
  accounts: BankAccount[];
  setAccounts: React.Dispatch<React.SetStateAction<BankAccount[]>>;
  /** Multi Bank Account (FIN-90) PR4: re-fetches the account list after a mapping change or
   * swap -- see BankAccountChartMappingCell's own doc comment for why this is never optimistic. */
  onMappingChanged: () => void | Promise<void>;
  /** FIN-1021/AC14: bankAccountId -> count of entries currently waiting on that account's
   * mapping, so BankAccountChartMappingCell can warn before a pick books and locks them. */
  waitingCountByAccountId?: Map<string, number>;
}

const BankAccountsDisplay = ({ accounts, setAccounts, onMappingChanged, waitingCountByAccountId }: BankAccountsDisplayProps) => {
  const navigate = useNavigate();
  const t = useTranslations('consultantBank');
  const accountTypeLabel = useBankAccountType();
  // FIN-568 (bank-account-importing-indicator): per-row indicator, checked from real persisted
  // status on every mount — not tied to whether this render happened to follow a connect action.
  const { statusByAccountId } = useCompanySyncStatus(accounts.map((a) => a.id));
  // FIN-863: Renew access on an Access needed row opens the global renewal dialog for it.
  const consentContext = useConsentContextOptional();

  function renderBalanceCell(account: BankAccount) {
    if (account.status === 'consent_invalid' && consentContext) {
      return (
        <Button
          variant="link"
          size="sm"
          className="h-auto p-0 font-semibold underline underline-offset-2"
          onClick={(event) => {
            // The row itself opens the account's details page.
            event.stopPropagation();
            consentContext.setConsentRequiredError(renewalRequestFor([account]));
          }}
        >
          {t('accounts.renewAccess')}
        </Button>
      );
    }
    if (account.syncFailed) {
      return <span className="text-muted-foreground font-normal">{t('accounts.unavailable')}</span>;
    }
    return (
      <div className="flex items-center gap-1">
        <Banknote className="h-4 w-4 text-muted-foreground" />
        {formatCurrency(account.balance, account.currency)}
      </div>
    );
  }
  const [openTransferDialog, setOpenTransferDialog] = useState(false);
  const [fromAccount, setFromAccount] = useState("");
  const [toAccount, setToAccount] = useState("");
  const [amount, setAmount] = useState("");
  const [isTransferring, setIsTransferring] = useState(false);
  const { getBankDisplayName: displayNameFor, getBankLogo: logoFor } = useBankCatalog();
  const [signingSession, setSigningSession] = useState<null | {
    signingBasketId: string;
    authorisationId: string;
    url: string;
    status: 'pending' | 'completed';
  }>(null);
  const [signingLoading, setSigningLoading] = useState(false);
  const [signingError, setSigningError] = useState<string | null>(null);

  // Get bank display name
  const getBankDisplayName = (account: BankAccount) => displayNameFor(account.bic, account.bankName);

  // "Nordea (••1234)" -- shared by the From/To account dropdowns below.
  const accountOptionLabel = (account: BankAccount) =>
    `${getBankDisplayName(account)} (${account.maskedNumber ?? t('noAccountNumber')})`;

  // Get bank logo
  const getBankLogo = (account: BankAccount) => logoFor(account.bic, account.logoUrl);

  // Handle money transfer
  const handleTransfer = async () => {
    if (!isMoneyTransferEnabled()) {
      toast.error(t('accounts.toasts.transferUnavailable'));
      return;
    }
    if (!fromAccount || !toAccount || !amount || parseFloat(amount) <= 0) {
      toast.error(t('accounts.toasts.fillAll'));
      return;
    }
    setIsTransferring(true);
    setSigningError(null);
    try {
      const paymentService = new PaymentService();
      const result = await paymentService.transferMoney({
        fromAccountId: fromAccount,
        toAccountId: toAccount,
        amount: parseFloat(amount),
        currency: accounts.find(a => a.id === fromAccount)?.currency || 'SEK',
      });
      setSigningSession({
        signingBasketId: result.signingBasketId,
        authorisationId: result.authorisationId,
        url: result.url,
        status: 'pending',
      });
    } catch (error: unknown) {
      setSigningError(userFacingErrorMessage(error, t('accounts.errors.transferFailed')));
      setIsTransferring(false);
      return;
    }
    setIsTransferring(false);
  };

  // Poll for signing status
  useEffect(() => {
    if (!signingSession || signingSession.status === 'completed') return;
    const poll = async () => {
      try {
        const paymentService = new PaymentService();
        // Find the fromAccount's bic for bicFi
        // TODO: Consider better way to get bicFi
        const fromAccountObj = accounts.find(a => a.id === fromAccount);
        const bicFi = fromAccountObj?.bic || '';
        const xRequestId = crypto.randomUUID();
        const status = await paymentService.getAuthorisationStatus(
          signingSession.signingBasketId,
          signingSession.authorisationId,
          xRequestId,
          bicFi,
          'transfer'
        );
        if (status.completed) {
          setSigningSession(s => s && { ...s, status: 'completed' });
          clearInterval(interval);
        }
      } catch (e: unknown) {
        if (e instanceof Error) {
          // Optionally handle polling error
          console.error('Polling error:', e.message);
        }
      }
    };
    const interval: NodeJS.Timeout = setInterval(poll, 2000);
    return () => clearInterval(interval);
  }, [signingSession, accounts, fromAccount]);

  // Reset transfer form
  const resetTransferForm = () => {
    setFromAccount("");
    setToAccount("");
    setAmount("");
  };

  // Handle transfer dialog change
  const handleTransferDialogChange = (open: boolean) => {
    setOpenTransferDialog(open);
    if (!open) {
      resetTransferForm();
    }
  };

  // Handle bank account row click to navigate to detail view
  const handleAccountClick = (accountId: string) => {
    navigate(`/bank-account/${accountId}`);
  };

  // Format currency
  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return formatAmount(amount, currency);
  };

  return (
    <>
      <div className="flex justify-between items-center">
        <CardTitle>{t('accounts.title')}</CardTitle>
        {isMoneyTransferEnabled() && (
          <Dialog open={openTransferDialog} onOpenChange={handleTransferDialogChange}>
            <DialogTrigger asChild>
              <Button variant="outline" className="flex items-center gap-2">
                <ArrowRight size={16} />
                {t('accounts.transferMoney')}
              </Button>
            </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('accounts.transferMoney')}</DialogTitle>
              <DialogDescription>
                {t('accounts.transferDescription')}
              </DialogDescription>
            </DialogHeader>
            {signingSession ? (
              <div className="flex flex-col items-center py-8">
                <h3 className="text-lg font-semibold mb-4">{t('accounts.scanToSign')}</h3>
                <div className="flex items-center justify-center h-48 w-48 bg-muted rounded-lg mb-4">
                  <QrCode className="h-16 w-16 text-muted-foreground" />
                </div>
                <div className="text-xs text-muted-foreground font-mono break-all mb-4">{signingSession.url}</div>
                {signingSession.status !== 'completed' && (
                  <div className="flex flex-col items-center gap-2">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">{t('accounts.waitingForAuthorisation')}</span>
                  </div>
                )}
                {signingSession.status === 'completed' && (
                  <div className="flex flex-col items-center gap-2">
                    <CheckCircle className="h-8 w-8 text-green-600" />
                    <span className="text-sm font-medium text-green-600">{t('accounts.transferSigned')}</span>
                    <Button onClick={() => { setSigningSession(null); setOpenTransferDialog(false); resetTransferForm(); }}>{t('accounts.close')}</Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <label htmlFor="fromAccount" className="text-sm font-medium">{t('accounts.fromAccount')}</label>
                  <Select value={fromAccount} onValueChange={setFromAccount}>
                    <SelectTrigger id="fromAccount" className="w-full">
                      <SelectValue placeholder={t('accounts.selectSource')} />
                    </SelectTrigger>
                    <SelectContent>
                      {accounts.map(account => (
                        <SelectItem key={`from-${account.id}`} value={account.id}>
                          {accountOptionLabel(account)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label htmlFor="toAccount" className="text-sm font-medium">{t('accounts.toAccount')}</label>
                  <Select value={toAccount} onValueChange={setToAccount}>
                    <SelectTrigger id="toAccount" className="w-full">
                      <SelectValue placeholder={t('accounts.selectDestination')} />
                    </SelectTrigger>
                    <SelectContent>
                      {accounts.map(account => (
                        <SelectItem key={`to-${account.id}`} value={account.id} disabled={account.id === fromAccount}>
                          {accountOptionLabel(account)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label htmlFor="amount" className="text-sm font-medium">{t('accounts.amount')}</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                    <Input 
                      id="amount"
                      type="number"
                      placeholder="0.00"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      className="pl-8"
                      min="0.01"
                      step="0.01"
                    />
                  </div>
                </div>
                {signingError && <div className="text-red-500 text-sm">{signingError}</div>}
                <Button 
                  onClick={handleTransfer} 
                  className="w-full mt-4" 
                  disabled={isTransferring || !fromAccount || !toAccount || !amount || parseFloat(amount) <= 0}
                >
                  {isTransferring ? t('accounts.processing') : t('accounts.transferMoney')}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
        )}
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('accounts.columns.bank')}</TableHead>
            <TableHead>{t('accounts.columns.accountNumber')}</TableHead>
            <TableHead>{t('accounts.columns.konto')}</TableHead>
            <TableHead>{t('accounts.columns.balance')}</TableHead>
            <TableHead>{t('accounts.columns.currency')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {accounts.map((account) => (
            <TableRow
              key={`account-${account.id}`}
              id={`bank-account-row-${account.id}`}
              onClick={() => handleAccountClick(account.id)}
              className="cursor-pointer hover:bg-muted"
            >
              <TableCell className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-md border p-1 flex items-center justify-center bg-white">
                  {getBankLogo(account) ? (
                    <img
                      src={getBankLogo(account)}
                      alt={t('accounts.logoAlt', { name: getBankDisplayName(account) })}
                      className="h-6 w-6 object-contain"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = '/placeholder.svg';
                      }}
                    />
                  ) : (
                    <CreditCard className="h-4 w-4" />
                  )}
                </div>
                <span>{getBankDisplayName(account)}</span>
                {account.accountType === 'personal' && (
                  <Badge data-testid={`account-personal-chip-${account.id}`} variant="secondary">
                    {t('accounts.personalChip')}
                  </Badge>
                )}
                {accountTypeLabel(account.cashAccountType) && (
                  <span className="text-xs text-muted-foreground">
                    {accountTypeLabel(account.cashAccountType)}
                  </span>
                )}
                {isSyncStillImporting(statusByAccountId.get(account.id)) && (
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    {t('accounts.importing')}
                  </span>
                )}
                {/* Also shown when the scheduler has given up (FIN-745): before that, an
                    exhausted retry budget left the row spinning "Importing…" forever, because
                    nothing ever moves it out of 'failed' again. */}
                {!isSyncStillImporting(statusByAccountId.get(account.id)) && account.status === 'consent_invalid' && (
                  <span className="flex items-center gap-1 text-xs text-destructive">
                    <span className="h-1.5 w-1.5 rounded-full bg-current" />
                    {t('accounts.accessNeeded')}
                  </span>
                )}
                {!isSyncStillImporting(statusByAccountId.get(account.id))
                  && account.status !== 'consent_invalid'
                  && (account.syncFailed || isSyncStopped(statusByAccountId.get(account.id))) && (
                  <span className="flex items-center gap-1 text-xs text-amber-600">
                    <AlertTriangle className="h-3 w-3" />
                    {t('accounts.couldntSync')}
                  </span>
                )}
              </TableCell>
              <TableCell>{account.maskedNumber ?? t('noAccountNumber')}</TableCell>
              <TableCell>
                <BankAccountChartMappingCell
                  account={account}
                  accounts={accounts}
                  onChanged={onMappingChanged}
                  waitingCount={waitingCountByAccountId?.get(account.id) ?? 0}
                />
              </TableCell>
              <TableCell className="font-medium">{renderBalanceCell(account)}</TableCell>
              <TableCell>{account.currency}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  );
};

export default BankAccountsDisplay;
